import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardBody, CardHeader } from './ui/Card';
import { CardListToolbar } from './ui/CardListToolbar';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { AlertMessage } from './ui/AlertMessage';
import { ConfirmDialog } from './ui/ConfirmDialog';
import { Modal } from './ui/Modal';
import { EmptyState } from './ui/EmptyState';
import { SkeletonRow } from './ui/Skeleton';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'http://localhost:8080';

const getInitialForm = () => ({
  name: '',
  contact: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
});

export function formatPartyAddressSummary(p) {
  if (!p) return '';
  const lines = [p.addressLine1, p.addressLine2].filter((x) => String(x || '').trim());
  const cs = [p.city, p.state].map((x) => String(x || '').trim()).filter(Boolean).join(', ');
  if (cs) lines.push(cs);
  return lines.join(' · ');
}

function PartyMaster({
  title,
  modalTitle,
  addButtonText,
  searchPlaceholder,
  partyCategory,
  namePlaceholder = 'Name',
}) {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('create');
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(getInitialForm);
  const [fieldErrors, setFieldErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [pageMessage, setPageMessage] = useState('');
  const [pageError, setPageError] = useState('');
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const headers = useMemo(() => {
    const token = localStorage.getItem('token');
    const h = { 'Content-Type': 'application/json' };
    if (token) {
      h.Authorization = `Bearer ${token}`;
    }
    return h;
  }, []);

  useEffect(() => {
    const fetchParties = async () => {
      setLoading(true);
      setPageError('');
      try {
        const response = await fetch(`${API_BASE_URL}/api/parties?category=${partyCategory}`, { headers });
        if (!response.ok) {
          throw new Error('Unable to load parties');
        }
        const data = await response.json();
        setItems(Array.isArray(data) ? data : []);
      } catch (err) {
        setItems([]);
        setPageError(err?.message || 'Unable to load parties');
      } finally {
        setLoading(false);
      }
    };
    fetchParties();
  }, [partyCategory, headers]);

  useEffect(() => {
    if (!pageMessage) {
      return undefined;
    }
    const timer = setTimeout(() => setPageMessage(''), 3500);
    return () => clearTimeout(timer);
  }, [pageMessage]);

  useEffect(() => {
    if (!pageError) {
      return undefined;
    }
    const timer = setTimeout(() => setPageError(''), 5000);
    return () => clearTimeout(timer);
  }, [pageError]);

  useEffect(() => {
    if (!formError) {
      return undefined;
    }
    const timer = setTimeout(() => setFormError(''), 5000);
    return () => clearTimeout(timer);
  }, [formError]);

  const filteredItems = items.filter((item) => {
    const key = search.trim().toLowerCase();
    if (!key) {
      return true;
    }
    return (
      String(item.name || '').toLowerCase().includes(key) ||
      String(item.contact || '').toLowerCase().includes(key) ||
      String(item.addressLine1 || '').toLowerCase().includes(key) ||
      String(item.addressLine2 || '').toLowerCase().includes(key) ||
      String(item.city || '').toLowerCase().includes(key) ||
      String(item.state || '').toLowerCase().includes(key)
    );
  });

  const resetForm = (mode = 'create', record = null) => {
    const nextForm = getInitialForm();
    if (mode === 'edit' && record) {
      setForm({
        name: record.name || '',
        contact: record.contact || '',
        addressLine1: record.addressLine1 || '',
        addressLine2: record.addressLine2 || '',
        city: record.city || '',
        state: record.state || '',
      });
    } else {
      setForm(nextForm);
    }
    setFieldErrors({});
    setFormError('');
  };

  const openCreateForm = () => {
    setFormMode('create');
    setEditingItem(null);
    resetForm('create');
    setShowForm(true);
  };

  const openEditForm = (record) => {
    setFormMode('edit');
    setEditingItem(record);
    resetForm('edit', record);
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setFormMode('create');
    setEditingItem(null);
    resetForm('create');
  };

  const updateField = (field, value) => {
    setFieldErrors((prev) => (prev[field] ? { ...prev, [field]: '' } : prev));
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const validateForm = () => {
    const nextErrors = {};
    const name = form.name.trim();
    if (!name) {
      nextErrors.name = 'Please enter name';
    }
    setFieldErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setPageMessage('');
    setPageError('');
    setFormError('');

    if (!validateForm()) {
      return;
    }

    try {
      const payload = {
        category: partyCategory,
        name: form.name.trim(),
        contact: form.contact.trim() || null,
        addressLine1: form.addressLine1.trim() || null,
        addressLine2: form.addressLine2.trim() || null,
        city: form.city.trim() || null,
        state: form.state.trim() || null,
      };
      setSaving(true);
      const isCreate = formMode === 'create';
      const response = await fetch(
        isCreate
          ? `${API_BASE_URL}/api/parties`
          : `${API_BASE_URL}/api/parties/${editingItem.id}`,
        {
          method: isCreate ? 'POST' : 'PUT',
          headers,
          body: JSON.stringify(payload),
        }
      );
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to save details');
      }
      const saved = await response.json();
      const nextItems = isCreate
        ? [saved, ...items]
        : items.map((item) => (item.id === saved.id ? saved : item));
      setItems(nextItems);
      setPageMessage(formMode === 'create' ? `${title} created successfully` : `${title} updated successfully`);
      closeForm();
    } catch (err) {
      setFormError(err?.message || 'Unable to save details');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!confirmDeleteItem) {
      return;
    }
    setDeleteLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/parties/${confirmDeleteItem.id}`, {
        method: 'DELETE',
        headers,
      });
      if (!response.ok) {
        const errData = await response.json().catch(() => ({}));
        throw new Error(errData?.error || errData?.message || 'Unable to delete record');
      }
      const nextItems = items.filter((item) => item.id !== confirmDeleteItem.id);
      setItems(nextItems);
      setPageMessage(`${title} deleted successfully`);
      setConfirmDeleteItem(null);
    } catch (err) {
      setPageError(err?.message || 'Unable to delete record');
    } finally {
      setDeleteLoading(false);
    }
  };

  return (
    <div>
      <Card>
        <CardHeader variant="gradient">
          <CardListToolbar
            tone="light"
            title={title}
            actions={
              <>
                <div className="w-full sm:w-64">
                  <Input
                    type="text"
                    placeholder={searchPlaceholder}
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    leftIcon={(
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    )}
                  />
                </div>
                <Button
                  type="button"
                  variant="primary"
                  onClick={openCreateForm}
                  leftIcon={(
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                    </svg>
                  )}
                >
                  {addButtonText}
                </Button>
              </>
            }
          />
        </CardHeader>

        <CardBody>
          <AlertMessage
            message={pageMessage}
            type="success"
            onClose={() => setPageMessage('')}
            className="mb-4"
          />
          <AlertMessage
            message={pageError}
            type="error"
            onClose={() => setPageError('')}
            className="mb-4"
          />

          {!loading && filteredItems.length === 0 ? (
            <EmptyState
              title={search ? 'No records match your search' : `No ${String(title).toLowerCase()} yet`}
              description={search ? 'Try a different search term.' : 'Get started by adding your first record.'}
              action={!search ? (
                <Button type="button" variant="primary" onClick={openCreateForm}>
                  {addButtonText}
                </Button>
              ) : null}
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm">
              <table className="table-modern">
                <thead>
                  <tr>
                    <th className="w-14">#</th>
                    <th>Name</th>
                    <th>Contact</th>
                    <th>Address</th>
                    <th className="text-right pr-6">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {loading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <SkeletonRow key={i} columns={5} />
                      ))
                    : filteredItems.map((item, index) => (
                        <tr key={item.id}>
                          <td className="text-slate-400 tabular-nums">{index + 1}</td>
                          <td className="font-medium text-slate-800">{item.name || '—'}</td>
                          <td className="text-slate-700 text-sm max-w-[140px]">
                            <span className="block truncate" title={item.contact || ''}>{item.contact || '—'}</span>
                          </td>
                          <td className="text-slate-600 max-w-[360px]">
                            <span className="block truncate" title={formatPartyAddressSummary(item)}>
                              {formatPartyAddressSummary(item) || '—'}
                            </span>
                          </td>
                          <td className="pr-6">
                            <div className="flex items-center justify-end gap-1.5">
                              <button
                                type="button"
                                onClick={() => openEditForm(item)}
                                className="icon-action icon-action-primary"
                                title="Edit"
                                aria-label={`Edit ${item.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                </svg>
                              </button>
                              <button
                                type="button"
                                onClick={() => setConfirmDeleteItem(item)}
                                className="icon-action icon-action-danger"
                                title="Delete"
                                aria-label={`Delete ${item.name}`}
                              >
                                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                </svg>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </CardBody>
      </Card>

      <Modal
        open={showForm}
        onClose={closeForm}
        size="xl"
        title={formMode === 'create' ? modalTitle : `Edit ${modalTitle.replace(/^Add\s+/, '')}`}
        description={formMode === 'create' ? 'Enter details and save.' : 'Update this record and save your changes.'}
        icon={(
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
        )}
        footer={(
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={() => resetForm(formMode, editingItem)}
              disabled={saving}
            >
              Reset
            </Button>
            <Button
              type="submit"
              form="party-form"
              variant="primary"
              loading={saving}
              disabled={saving}
            >
              {formMode === 'create' ? 'Create' : 'Update'}
            </Button>
          </>
        )}
      >
        <form id="party-form" onSubmit={handleSubmit} noValidate className="py-6 space-y-5">
          <AlertMessage
            message={formError}
            type="error"
            onClose={() => setFormError('')}
          />

          <Input
            label="Name *"
            placeholder={namePlaceholder}
            value={form.name}
            onChange={(e) => updateField('name', e.target.value)}
            error={fieldErrors.name}
            required
          />

          <div className="space-y-1.5">
            <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Address</p>
            <div className="grid grid-cols-1 gap-3">
              <Input
                label="Line 1"
                placeholder="Street, building, area…"
                value={form.addressLine1}
                onChange={(e) => updateField('addressLine1', e.target.value)}
              />
              <Input
                label="Line 2"
                placeholder="Additional line (optional)"
                value={form.addressLine2}
                onChange={(e) => updateField('addressLine2', e.target.value)}
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Input
                  label="City"
                  placeholder="City"
                  value={form.city}
                  onChange={(e) => updateField('city', e.target.value)}
                />
                <Input
                  label="State"
                  placeholder="State"
                  value={form.state}
                  onChange={(e) => updateField('state', e.target.value)}
                />
              </div>
            </div>
          </div>

          <Input
            label="Contact"
            placeholder="Phone, WhatsApp, email…"
            value={form.contact}
            onChange={(e) => updateField('contact', e.target.value)}
            hint="Optional — any contact you use for this party."
          />
        </form>
      </Modal>

      <ConfirmDialog
        open={Boolean(confirmDeleteItem)}
        title="Delete Record"
        message={confirmDeleteItem ? `Do you want to delete "${confirmDeleteItem.name}"?` : ''}
        confirmText="Delete"
        cancelText="Cancel"
        onConfirm={handleDelete}
        onCancel={() => setConfirmDeleteItem(null)}
        loading={deleteLoading}
        variant="danger"
      />
    </div>
  );
}

export default PartyMaster;
